// ============================================================
// documentFonts.ts — the curated FONTS for the firm's documents (bill PDF,
// credit notes, client statement, case summary export) —
// Firm.documentBranding.headingFont / bodyFont. The app UI is unaffected.
//
// Every font is SIL Open Font License 1.1 (licence text beside the files,
// <id>-OFL.txt) and BUNDLED with the app — never fetched from Google: the
// print tab loads public/document-fonts/*, the server embeds
// backend/assets/document-fonts/* as data URIs (lib/documentFontFiles). Same
// files on both sides, so the server PDF and the print tab use identical
// glyphs. ★ KEEP THE TWO FOLDERS IDENTICAL.
//
// Size: static WOFF2 per weight, only the weights the documents use —
// body 400 / 500 / 600 / 700, headings 600 / 700 / 800 — each split into Latin + Latin Extended with
// unicode-range, so Latin Extended (accented client names) loads only when
// the text needs it.
//
// No font chosen (or an unknown id) = DM Sans — what the documents have
// always used — so nothing changes unless a firm picks a font.
//
// ★ KEEP IN SYNC with backend/src/lib/documentFonts.ts (identical logic).
// ============================================================

export interface DocumentFont {
  id: string           // stored key + file prefix
  name: string         // shown in the admin dropdown
  kind: 'sans' | 'serif'
  weights: number[]    // weights bundled
}

export const DOCUMENT_FONTS: readonly DocumentFont[] = [
  { id: 'dm-sans', name: 'DM Sans', kind: 'sans', weights: [400, 500, 600, 700, 800] },
  { id: 'inter', name: 'Inter', kind: 'sans', weights: [400, 500, 600, 700, 800] },
  { id: 'source-sans-3', name: 'Source Sans 3', kind: 'sans', weights: [400, 500, 600, 700, 800] },
  { id: 'libre-franklin', name: 'Libre Franklin', kind: 'sans', weights: [400, 500, 600, 700, 800] },
  { id: 'source-serif-4', name: 'Source Serif 4', kind: 'serif', weights: [400, 500, 600, 700, 800] },
  { id: 'lora', name: 'Lora', kind: 'serif', weights: [400, 500, 600, 700] },
  { id: 'eb-garamond', name: 'EB Garamond', kind: 'serif', weights: [400, 500, 600, 700, 800] },
  { id: 'merriweather', name: 'Merriweather', kind: 'serif', weights: [400, 500, 600, 700, 800] },
]
export const DEFAULT_DOCUMENT_FONT = 'dm-sans'

// A stored / posted font id → a known id, or null (= the default).
export const normaliseFontId = (v: unknown): string | null =>
  typeof v === 'string' && DOCUMENT_FONTS.some((f) => f.id === v) ? v : null
const fontOf = (id: string | null | undefined): DocumentFont =>
  DOCUMENT_FONTS.find((f) => f.id === id) ?? DOCUMENT_FONTS[0]

const BODY_WEIGHTS = [400, 500, 600, 700]
const HEADING_WEIGHTS = [600, 700, 800]
// (B4 follow-ups) The heading weights. DM Sans is bundled only in its 9pt
// optical size (side by side it matches the app's Google Fonts DM Sans), whose
// 700 / 800 read heavy at heading sizes next to the app's semibold headings —
// so with DM Sans every heading is a real 600 (design/fonts/heading-options.png).
// A font a firm picked keeps the weights it was chosen at (800 / 700).
export interface HeadingWeights { strong: number; bold: number }
export const headingWeightsOf = (id: string | null | undefined): HeadingWeights =>
  fontOf(id).id === 'dm-sans' ? { strong: 600, bold: 600 } : { strong: 800, bold: 700 }
// Google Fonts' subset ranges (as the files were cut).
const SUBSETS = [
  { key: 'latin', range: 'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD' },
  { key: 'latin-ext', range: 'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF' },
]
// Text that needs the Latin Extended files (the server embeds them only then).
export const needsLatinExt = (text: string): boolean => /[Ā-˿ᴀ-ỿ†₠-₫₭-⃀ℓⱠ-Ɀ꜠-ꟿ]/.test(text)

// The family name used in the documents' CSS — prefixed, so a copy of the
// font installed on the viewer's machine can never stand in for the bundled one.
const family = (f: DocumentFont): string => `Casevo Doc ${f.name}`
// CSS font-family value: the bundled face, then a like-for-like fallback.
export const fontStack = (id: string | null | undefined): string => {
  const f = fontOf(id)
  return `'${family(f)}', ${f.kind === 'serif' ? "Georgia, 'Times New Roman', serif" : "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"}`
}
export const fontFileName = (id: string, subset: string, weight: number): string => `${id}-${subset}-${weight}.woff2`

// (batch 2) Two document-wide guards, appended after the @font-face rules so
// every document gets them:
//   • font-synthesis: none — a weight that isn't bundled for its family prints
//     as the nearest REAL weight (and fails documentFontWeights.test) instead
//     of a smeared, browser-faked bold;
//   • b / strong at a fixed 700 — the UA's `bolder` asks for 900 inside a
//     600 / 700 parent, a weight no family bundles.
export const DOCUMENT_FONT_GUARDS = 'html { font-synthesis: none; } b, strong { font-weight: 700; }'

export interface DocumentFontsCss {
  heading: string  // font-family for headings (firm name, title, section heads)
  body: string     // font-family for everything else
  faces: string    // the @font-face rules
  // Heading weights: `strong` the firm name / statement title (was 800),
  // `bold` the bill title and section heads (was 700) — headingWeightsOf.
  weights: HeadingWeights
}
// The @font-face rules for the chosen pair — only the weights each role uses.
// `src` turns a file name into a URL (print tab) or a data URI (server);
// `ext: false` leaves out the Latin Extended faces.
export const documentFontsCss = (
  headingFont: string | null | undefined, bodyFont: string | null | undefined,
  src: (file: string) => string, opts: { ext?: boolean } = {},
): DocumentFontsCss => {
  const heading = fontOf(headingFont)
  const body = fontOf(bodyFont)
  const want = new Map<DocumentFont, Set<number>>()
  const add = (f: DocumentFont, ws: number[]): void => {
    const set = want.get(f) ?? new Set<number>()
    // A weight the family lacks (e.g. Lora 800) → the browser's nearest bundled one.
    ws.filter((w) => f.weights.includes(w)).forEach((w) => set.add(w))
    want.set(f, set)
  }
  add(body, BODY_WEIGHTS)
  add(heading, HEADING_WEIGHTS)
  const faces: string[] = []
  want.forEach((weights, f) => {
    Array.from(weights).sort((a, b) => a - b).forEach((w) => {
      SUBSETS.filter((s) => opts.ext !== false || s.key === 'latin').forEach((s) => {
        faces.push(`@font-face { font-family: '${family(f)}'; font-style: normal; font-weight: ${w}; font-display: block; src: url("${src(fontFileName(f.id, s.key, w))}") format('woff2'); unicode-range: ${s.range}; }`)
      })
    })
  })
  faces.push(DOCUMENT_FONT_GUARDS)
  return { heading: fontStack(heading.id), body: fontStack(body.id), faces: faces.join('\n'), weights: headingWeightsOf(heading.id) }
}
